import DOMPurify from "dompurify";

const RICH_HTML_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "blockquote",
  "ol",
  "ul",
  "li",
  "a",
  "img",
  "video",
  "source",
  "h1",
  "h2",
  "h3",
  "span",
  "pre",
  "code",
] as const;

const RICH_HTML_ALLOWED_ATTR = [
  "href",
  "src",
  "alt",
  "class",
  "target",
  "rel",
  "title",
  "aria-label",
  "controls",
  "width",
  "height",
] as const;

const RICH_HTML_FORBID_TAGS = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
] as const;

const RICH_HTML_FORBID_ATTR = [
  "style",
  "srcdoc",
  "onabort",
  "onblur",
  "onchange",
  "onclick",
  "onerror",
  "onfocus",
  "oninput",
  "onkeydown",
  "onkeypress",
  "onkeyup",
  "onload",
  "onmousedown",
  "onmouseenter",
  "onmouseleave",
  "onmousemove",
  "onmouseout",
  "onmouseover",
  "onmouseup",
  "onsubmit",
] as const;

const SAFE_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;

export const sanitizeRichHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: [...RICH_HTML_ALLOWED_TAGS],
    ALLOWED_ATTR: [...RICH_HTML_ALLOWED_ATTR],
    FORBID_TAGS: [...RICH_HTML_FORBID_TAGS],
    FORBID_ATTR: [...RICH_HTML_FORBID_ATTR],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
  });


const PLAIN_TEXT_URL_REGEXP = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_URL_PUNCTUATION_REGEXP = /[.,;:!?]+$/;

function shouldSkipAutolinkNode(textNode: Text): boolean {
  const parent = textNode.parentElement;
  if (!parent) return true;
  return Boolean(
    parent.closest("a, pre, code, button, input, textarea, select"),
  );
}

function appendAutolinkedText(
  fragment: DocumentFragment,
  ownerDocument: Document,
  text: string,
): void {
  PLAIN_TEXT_URL_REGEXP.lastIndex = 0;
  let lastIndex = 0;
  let match = PLAIN_TEXT_URL_REGEXP.exec(text);

  while (match) {
    const rawUrl = match[0];
    const matchIndex = match.index;
    const cleanUrl = rawUrl.replace(TRAILING_URL_PUNCTUATION_REGEXP, "");
    const trailingText = rawUrl.slice(cleanUrl.length);

    if (matchIndex > lastIndex) {
      fragment.appendChild(
        ownerDocument.createTextNode(text.slice(lastIndex, matchIndex)),
      );
    }

    if (cleanUrl) {
      const anchor = ownerDocument.createElement("a");
      anchor.href = cleanUrl;
      anchor.textContent = cleanUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.className = "rich-inline-link";
      fragment.appendChild(anchor);
    }

    if (trailingText) {
      fragment.appendChild(ownerDocument.createTextNode(trailingText));
    }

    lastIndex = matchIndex + rawUrl.length;
    match = PLAIN_TEXT_URL_REGEXP.exec(text);
  }

  if (lastIndex < text.length) {
    fragment.appendChild(ownerDocument.createTextNode(text.slice(lastIndex)));
  }
}

export const linkifyPlainTextUrlsInHtml = (
  html: string | null | undefined,
): string => {
  const sanitizedHtml = sanitizeRichHtml(html);
  if (!sanitizedHtml || typeof document === "undefined") return sanitizedHtml;

  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;

  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!(node instanceof Text)) return NodeFilter.FILTER_REJECT;
        if (shouldSkipAutolinkNode(node)) return NodeFilter.FILTER_REJECT;
        PLAIN_TEXT_URL_REGEXP.lastIndex = 0;
        return PLAIN_TEXT_URL_REGEXP.test(node.nodeValue || "")
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    },
  );

  let current = walker.nextNode();
  while (current) {
    if (current instanceof Text) textNodes.push(current);
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const ownerDocument = textNode.ownerDocument;
    const fragment = ownerDocument.createDocumentFragment();
    appendAutolinkedText(fragment, ownerDocument, textNode.nodeValue || "");
    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return sanitizeRichHtml(container.innerHTML);
};

export const sanitizePlainText = (value: string | null | undefined): string =>
  DOMPurify.sanitize(value || "", { ALLOWED_TAGS: [] }).trim();
